import React, { forwardRef, useImperativeHandle, useState, useRef, useCallback } from 'react';
import { 
  Box, 
  Checkbox, 
  ListItemText,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface AssociationVehicleTypeFilterProps {
  api: any;
  column: any;
  columnApi: any;
  context: any;
}

// Option type: { value: string, label: string }
// value: `${vehicleType}|||${vehicleSubType}`

const AssociationVehicleTypeFilter = forwardRef((props: AssociationVehicleTypeFilterProps, ref) => {
  const { t } = useTranslation();
  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [dynamicOptions, setDynamicOptions] = useState<{ value: string, label: string }[]>([]);
  const currentFilterRef = useRef<string[]>([]);

  // Extract unique vehicleType-vehicleSubType pairs from visible rows
  const updateOptions = useCallback(() => {
    const pairs: { vehicleType: string, vehicleSubType: string }[] = [];
    props.api.forEachNodeAfterFilterAndSort((node: any) => {
      if (node.data && node.data.vehicleType && node.data.vehicleSubType) {
        pairs.push({
          vehicleType: node.data.vehicleType,
          vehicleSubType: node.data.vehicleSubType
        });
      }
    });
    // Unique by vehicleType + vehicleSubType
    const unique = Array.from(new Set(pairs.map(p => `${p.vehicleType}|||${p.vehicleSubType}`)))
      .map(val => {
        const [vehicleType, vehicleSubType] = val.split('|||');
        const typeLabel = t(`screens.fleetAndRoute.vehicles.${vehicleType}`, vehicleType);
        const subTypeLabel = t(`screens.fleetAndRoute.vehicles.${vehicleSubType}`, vehicleSubType);
        return {
          value: val,
          label: `${typeLabel} - ${subTypeLabel}`
        };
      });
    setDynamicOptions(unique);
  }, [props.api, t]);

  React.useEffect(() => {
    updateOptions();
    // eslint-disable-next-line
  }, [props.api, t, props.api.getDisplayedRowCount()]);

  useImperativeHandle(ref, () => ({
    isFilterActive() {
      return currentFilterRef.current.length > 0;
    },
    doesFilterPass(params: any) {
      if (currentFilterRef.current.length === 0) return true;
      const val = `${params.data.vehicleType}|||${params.data.vehicleSubType}`;
      return currentFilterRef.current.includes(val);
    },
    getModel() {
      if (this.isFilterActive()) {
        return {
          type: 'set',
          values: currentFilterRef.current,
        };
      }
      return null;
    },
    setModel(model: any) {
      const newValues = model?.values || [];
      setSelectedValues(newValues);
      currentFilterRef.current = newValues;
    },
    getModelAsString() {
      if (currentFilterRef.current.length === 0) return '';
      return currentFilterRef.current
        .map(val => {
          const [vehicleType, vehicleSubType] = val.split('|||');
          const typeLabel = t(`screens.fleetAndRoute.vehicles.${vehicleType}`, vehicleType);
          const subTypeLabel = t(`screens.fleetAndRoute.vehicles.${vehicleSubType}`, vehicleSubType);
          return `${typeLabel} - ${subTypeLabel}`;
        })
        .join(', ');
    }
  }), [t]);

  const handleCheckboxChange = (value: string) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    setSelectedValues(newValues);
    currentFilterRef.current = newValues;
    props.api.onFilterChanged();
  };

  const handleReset = () => {
    setSelectedValues([]);
    currentFilterRef.current = [];
    props.api.onFilterChanged();
  };

  return (
    <Box sx={{ 
      width: '180px',
      bgcolor: 'background.paper',
      borderRadius: 1,
      boxShadow: 1
    }}>
      <List sx={{ 
        py: 0,
        maxHeight: '200px',
        overflowY: 'auto'
      }}>
        {dynamicOptions.map((option) => (
          <ListItem key={option.value} disablePadding>
            <ListItemButton
              dense
              onClick={() => handleCheckboxChange(option.value)}
              sx={{
                minHeight: '28px',
                py: 0.5,
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: '28px' }}>
                <Checkbox
                  edge="start"
                  checked={selectedValues.includes(option.value)}
                  tabIndex={-1}
                  disableRipple
                  size="small"
                  sx={{ 
                    padding: '2px',
                    '& .MuiSvgIcon-root': {
                      fontSize: '16px'
                    }
                  }}
                />
              </ListItemIcon>
              <ListItemText 
                primary={option.label}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.primary',
                  sx: {
                    fontSize: '0.75rem',
                    lineHeight: 1.2
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          p: 0.5
        }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={handleReset}
          sx={{ 
            minWidth: '60px',
            textTransform: 'none',
            fontSize: '0.75rem',
            py: 0.25,
            px: 1,
            borderColor: 'divider',
            color: '#CF0A2C',
            '&:hover': {
              borderColor: '#CF0A2C',
              backgroundColor: 'action.hover'
            }
          }}
        >
          {t('common.reset')}
        </Button>
      </Box>
    </Box>
  );
});

export default AssociationVehicleTypeFilter; 